import { MRT_ColumnDef } from 'material-react-table';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ROUTER_PATHS,
  TABLE_CONSTANTS_COLUMS,
  emptyCellOneLevel,
  formatDateWithTime,
  hasAllPermissions,
  hasPermission,
  useCheckPermissions,
  useTableFilter,
  useTableServerSideFiltering,
} from '@/shared';
import {
  CustomSearch,
  GeneralTable,
  SingleTableBoxScene,
  SwitchComponente,
} from '@/shared/components/ui';
import { Tarjeta } from '@/shared/interfaces';
import { useUiConfirmModalStore } from '@/store/ui';
import { useFetchTarjetas, useUpdateTarjeta } from '@/shared/actions/erp/administration/tarjeta.actions';
import { PermissionsEnum } from '@/auth/interfaces';


export interface TarjetasPageInterface { }

const TarjetasPage: React.FC<TarjetasPageInterface> = () => {
  useCheckPermissions(PermissionsEnum.api_view_tarjeta);
  const returnUrlTarjetaPage = ROUTER_PATHS.administration.tarjetas_nav;


  const navigate = useNavigate();

  ///* global state
  const setConfirmDialog = useUiConfirmModalStore(s => s.setConfirmDialog);
  const setConfirmDialogIsOpen = useUiConfirmModalStore(
    s => s.setConfirmDialogIsOpen
  );

  ///* table
  const {
    globalFilter,
    pagination,
    searchTerm,
    onChangeFilter,
    setPagination,
  } = useTableFilter();
  const { pageIndex, pageSize } = pagination;

  // server side filters - colums table
  const { filterObject, columnFilters, setColumnFilters } =
    useTableServerSideFiltering();

  ///* fetch data
  const {
    data: TarjetasPagingRes,
    isLoading,
    isRefetching,
  } = useFetchTarjetas({
    page: pageIndex + 1,
    page_size: pageSize,
    nombre: searchTerm,
    ...filterObject,
    filterByState: false,
  });

  ///* mutations
  const updateTarjetaMutation = useUpdateTarjeta(navigate, returnUrlTarjetaPage);

  ///* handlers
  const onEdit = (original: Tarjeta) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Editar Tarjeta',
      subtitle: '¿Está seguro que desea editar este registro?',
      onConfirm: () => {
        setConfirmDialogIsOpen(false);
        navigate(`${returnUrlTarjetaPage}/editar/${original.id_tarjeta}`);
      },
    });
  };

  ///* columns
  const columns = useMemo<MRT_ColumnDef<Tarjeta>[]>(
    () => [
      {
        accessorKey: 'numero_referencia',
        header: 'Número de Referencia',
        size: 180,
        Cell: ({ row }) => emptyCellOneLevel(row, 'numero_referencia'),
      },

      {
        accessorKey: 'nombre',
        header: 'Nombre',
        size: 180,
        Cell: ({ row }) => emptyCellOneLevel(row, 'nombre'),
      },
     /*  {
        accessorKey: 'codigo',
        header: 'Código',
        size: 180,
        Cell: ({ row }) => emptyCellOneLevel(row, 'codigo'),
      }, */
      {
        accessorKey: 'estado',
        header: 'Estado',
        size: 200,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ row }) => (
          <SwitchComponente
            estadoSwitch={row.original.estado}
            titulo={row.original.estado ? 'Activo' : 'Inactivo'}
            setEstadoSwitch={Tarjeta => {
              if (!hasPermission(PermissionsEnum.api_change_tarjeta)) return;
              setConfirmDialog({
                isOpen: true,
                title: 'Cambiar Estado',
                subtitle:
                  '¿Está seguro que desea cambiar el estado de este registro ?',
                onConfirm: () => {
                  setConfirmDialogIsOpen(false);
                  updateTarjetaMutation.mutate({
                    id: row.original.id_tarjeta!,
                    data: {
                      ...row.original,
                      estado: Tarjeta as boolean,
                    },
                  });
                },
              });
            }}
          />
        ),
      },
      {
        accessorKey: 'fecha_creacion',
        header: 'Fecha de Creación',
        size: 200,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ row }) =>
          row.original?.fecha_creacion
            ? formatDateWithTime(row.original.fecha_creacion)
            : 'N/A',
      },
      {
        accessorKey: 'fecha_modificacion',
        header: 'Fecha de Modificación',
        size: 200,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ row }) =>
          row.original?.fecha_modificacion
            ? formatDateWithTime(row.original.fecha_modificacion)
            : 'N/A',
      },
    ],
    []
  );

  return (
    <SingleTableBoxScene
      title="Tarjeta"
      urlRedirect={`${returnUrlTarjetaPage}/crear`}
      enableRedirect={hasPermission(PermissionsEnum.api_add_tarjeta)}
    >
      <CustomSearch
        onChange={onChangeFilter}
        value={globalFilter}
        text="Tarjeta por nombre"
      />

      <GeneralTable<Tarjeta>
        columns={columns}
        data={TarjetasPagingRes?.data?.data || []}
        //
        isLoading={isLoading}
        isRefetching={isRefetching}
        // // search
        enableGlobalFilter={false}
        // // filters - llega al back
        enableManualFiltering={true}
        columnFilters={columnFilters}
        onColumnFiltersChange={setColumnFilters}
        // // pagination
        pagination={pagination}
        onPaging={setPagination}
        rowCount={TarjetasPagingRes?.data?.count}
        // // actions ========
        actionsColumnSize={TABLE_CONSTANTS_COLUMS.ACTIONCOLUMN_WIDTH}
        positionActionsColumn="first"
        enableColumnActions={hasAllPermissions([
          PermissionsEnum.api_change_tarjeta,
        ])}
        // crud
        onEdit={onEdit}
        canEdit={hasAllPermissions([
          PermissionsEnum.api_change_tarjeta,
        ])}
        canDelete={false}
      />
    </SingleTableBoxScene>
  );
};

export default TarjetasPage;
